import React, { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  UserCog,
  X,
  Calendar,
  TreePine,
  CheckCircle2,
  Clock,
  Search,
  CalendarDays,
} from "lucide-react";
import { toast } from "sonner";
import { errorMsg } from "@/lib/utils-app";

export default function SupervisorInchargePage() {
  // In-Charge Assignment & Dashboard state
  const [dashboardData, setDashboardData] = useState(null);
  const [loadingDashboard, setLoadingDashboard] = useState(true);
  const [parks, setParks] = useState([]);
  const [selectedParkId, setSelectedParkId] = useState("");
  const [selectedInchargeEmpId, setSelectedInchargeEmpId] = useState("");
  const [assignedPersonnelList, setAssignedPersonnelList] = useState([]);
  const [selectedAssignmentDate, setSelectedAssignmentDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [assigningDate, setAssigningDate] = useState(false);

  // Filters
  const [filterParkId, setFilterParkId] = useState("__all__");
  const [filterDate, setFilterDate] = useState("");
  const [filterSearch, setFilterSearch] = useState("");

  // Load Parks & Dashboard data
  const loadDashboard = async () => {
    setLoadingDashboard(true);
    try {
      const params = {};
      if (filterParkId && filterParkId !== "__all__") params.park_id = filterParkId;
      if (filterDate) params.date = filterDate;
      if (filterSearch) params.search = filterSearch;

      const [dashRes, parksRes] = await Promise.all([
        api.get("/admin/supervisor-incharge/dashboard", { params }),
        api.get("/parks"),
      ]);
      setDashboardData(dashRes.data);
      const activeParks = (parksRes.data || []).filter((p) => p.active !== false);
      setParks(activeParks);
    } catch (e) {
      console.error("Failed to load dashboard:", e);
      toast.error(errorMsg(e, "Failed to load Supervisor In-Charge dashboard"));
    } finally {
      setLoadingDashboard(false);
    }
  };

  // Load In-Charge personnel for selected park in assignment section
  const loadParkIncharge = async (parkId) => {
    if (!parkId) {
      setAssignedPersonnelList([]);
      return;
    }
    try {
      const res = await api.get(`/admin/parks/${parkId}/incharge`);
      setAssignedPersonnelList(res.data || []);
      if (res.data && res.data.length > 0) {
        setSelectedInchargeEmpId(res.data[0].id);
      } else {
        setSelectedInchargeEmpId("");
      }
    } catch (e) {
      console.error("Failed to load assigned incharge personnel:", e);
    }
  };

  useEffect(() => {
    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterParkId, filterDate, filterSearch]);

  useEffect(() => {
    if (selectedParkId) {
      loadParkIncharge(selectedParkId);
    } else {
      setAssignedPersonnelList([]);
    }
  }, [selectedParkId]);

  // Handle Date-wise Supervisor In-Charge Assignment
  const handleAssignDateIncharge = async (empId) => {
    if (!selectedParkId) {
      toast.error("Please select a park first");
      return;
    }
    const targetEmpId = empId || selectedInchargeEmpId;
    if (!targetEmpId) {
      toast.error("Please select an assigned In-Charge employee");
      return;
    }
    if (!selectedAssignmentDate) {
      toast.error("Please select an assignment date");
      return;
    }

    setAssigningDate(true);
    try {
      await api.post("/admin/supervisor-incharge/assign", {
        park_id: selectedParkId,
        employee_id: targetEmpId,
        date: selectedAssignmentDate,
      });
      const parkObj = parks.find((p) => p.id === selectedParkId);
      toast.success(`Supervisor In-Charge assigned for ${parkObj?.name || "park"} on ${selectedAssignmentDate}`);
      await loadDashboard();
    } catch (e) {
      toast.error(errorMsg(e, "Failed to complete date assignment"));
    } finally {
      setAssigningDate(false);
    }
  };

  // Handle deleting date assignment
  const handleDeleteDateAssignment = async (assignmentId) => {
    try {
      await api.delete(`/admin/supervisor-incharge/assignments/${assignmentId}`);
      toast.success("Date assignment removed");
      await loadDashboard();
    } catch (e) {
      toast.error(errorMsg(e, "Failed to remove date assignment"));
    }
  };

  return (
    <AppShell
      title="Supervisor In-charge"
      subtitle="Manage Park-wise Supervisor In-Charge assignments, date scheduling, and real-time dashboard"
    >
      <div className="flex flex-col gap-5">
        {/* DASHBOARD KPI CARDS */}
        <div className="grid grid-cols-2 gap-3.5 sm:grid-cols-4" data-testid="incharge-kpi-dashboard">
          <div className="flex items-center gap-3.5 rounded-xl border border-slate-200 bg-white p-3.5 shadow-sm transition-all hover:shadow-md">
            <div className="grid h-11 w-11 shrink-0 place-items-center rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700">
              <TreePine className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Total Parks</div>
              <div className="font-mono text-2xl font-bold tabular-nums text-slate-900" data-testid="kpi-total-parks">
                {loadingDashboard ? "…" : dashboardData?.total_parks ?? 0}
              </div>
              <div className="text-[10px] text-slate-400">Industrial parks</div>
            </div>
          </div>

          <div className="flex items-center gap-3.5 rounded-xl border border-slate-200 bg-white p-3.5 shadow-sm transition-all hover:shadow-md">
            <div className="grid h-11 w-11 shrink-0 place-items-center rounded-lg border border-sky-200 bg-sky-50 text-sky-700">
              <UserCog className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Total Assigned In-Charge</div>
              <div className="font-mono text-2xl font-bold tabular-nums text-sky-950" data-testid="kpi-total-incharge">
                {loadingDashboard ? "…" : dashboardData?.total_assigned_incharge ?? 0}
              </div>
              <div className="text-[10px] text-slate-400">Active personnel</div>
            </div>
          </div>

          <div className="flex items-center gap-3.5 rounded-xl border border-slate-200 bg-white p-3.5 shadow-sm transition-all hover:shadow-md">
            <div className="grid h-11 w-11 shrink-0 place-items-center rounded-lg border border-amber-200 bg-amber-50 text-amber-700">
              <Clock className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Total Unassigned Parks</div>
              <div className="font-mono text-2xl font-bold tabular-nums text-amber-900" data-testid="kpi-unassigned-parks">
                {loadingDashboard ? "…" : dashboardData?.total_unassigned_parks ?? 0}
              </div>
              <div className="text-[10px] text-amber-700">Pending In-Charge</div>
            </div>
          </div>

          <div className="flex items-center gap-3.5 rounded-xl border border-slate-200 bg-white p-3.5 shadow-sm transition-all hover:shadow-md">
            <div className="grid h-11 w-11 shrink-0 place-items-center rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-700">
              <CheckCircle2 className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Assignment Status</div>
              <div className="mt-1 flex items-center gap-1.5">
                <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-900 text-[11px]" data-testid="kpi-status-assigned">
                  Assigned: {dashboardData?.assignment_status?.assigned ?? 0}
                </Badge>
                <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-900 text-[11px]" data-testid="kpi-status-pending">
                  Pending: {dashboardData?.assignment_status?.pending ?? 0}
                </Badge>
              </div>
            </div>
          </div>
        </div>

        {/* SEARCH AND FILTER BAR */}
        <Card>
          <CardContent className="p-3 grid gap-3 sm:grid-cols-3 items-center">
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Filter by Park</label>
              <Select value={filterParkId} onValueChange={setFilterParkId}>
                <SelectTrigger data-testid="incharge-park-filter">
                  <SelectValue placeholder="All Parks" />
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  <SelectItem value="__all__">All Parks</SelectItem>
                  {parks.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.code} — {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Filter by Date</label>
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={filterDate}
                  onChange={(e) => setFilterDate(e.target.value)}
                  className="h-9"
                  data-testid="incharge-date-filter"
                />
                {filterDate && (
                  <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={() => setFilterDate("")} title="Clear date filter">
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>

            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Search In-Charge Name / Park</label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search name or park code…"
                  value={filterSearch}
                  onChange={(e) => setFilterSearch(e.target.value)}
                  className="pl-9 h-9"
                  data-testid="incharge-search-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* WORKSPACE & ASSIGNMENT CONTROL PANEL */}
        <Card className="border-sky-200 bg-sky-50/20" data-testid="incharge-assignment-section">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base font-semibold text-sky-950">
              <CalendarDays className="h-5 w-5 text-sky-700" />
              Supervisor In-Charge Date Assignment Workspace
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-3">
              {/* 1. Park Selector */}
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-700 mb-1 block">
                  1. Select Park
                </label>
                <Select value={selectedParkId} onValueChange={setSelectedParkId}>
                  <SelectTrigger className="bg-white" data-testid="incharge-assign-park-select">
                    <SelectValue placeholder="Choose a park…" />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    {parks.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.code} — {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* 2. Assigned In-Charge Personnel Display */}
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-700 mb-1 block">
                  2. Assigned In-Charge Personnel
                </label>
                {!selectedParkId ? (
                  <div className="h-9 flex items-center px-3 rounded-md border border-slate-200 bg-slate-100 text-xs text-slate-500">
                    Select a park first
                  </div>
                ) : assignedPersonnelList.length === 0 ? (
                  <div className="h-9 flex items-center px-3 rounded-md border border-amber-200 bg-amber-50 text-xs text-amber-900 font-medium">
                    No In-Charge personnel assigned for this park yet.
                  </div>
                ) : (
                  <Select value={selectedInchargeEmpId} onValueChange={setSelectedInchargeEmpId}>
                    <SelectTrigger className="bg-white" data-testid="incharge-assign-personnel-select">
                      <SelectValue placeholder="Select assigned in-charge…" />
                    </SelectTrigger>
                    <SelectContent>
                      {assignedPersonnelList.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.full_name} ({emp.designation || "Sweeper/Driver"})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* 3. Calendar Icon & Date Selector + Assign Button */}
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-700 mb-1 block">
                  3. Select Date & Assign
                </label>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Calendar className="absolute left-2.5 top-2.5 h-4 w-4 text-sky-700 pointer-events-none" />
                    <Input
                      type="date"
                      value={selectedAssignmentDate}
                      onChange={(e) => setSelectedAssignmentDate(e.target.value)}
                      className="pl-9 h-9 bg-white"
                      data-testid="incharge-assign-date-input"
                    />
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleAssignDateIncharge()}
                    disabled={assigningDate || !selectedParkId || !selectedInchargeEmpId}
                    className="h-9 bg-sky-700 hover:bg-sky-800 text-white font-medium"
                    data-testid="incharge-assign-confirm-button"
                  >
                    <CheckCircle2 className="mr-1.5 h-4 w-4" />
                    {assigningDate ? "Assigning…" : "Assign"}
                  </Button>
                </div>
              </div>
            </div>

            {/* List of assigned In-Charge personnel for selected park */}
            {selectedParkId && assignedPersonnelList.length > 0 && (
              <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3.5">
                <div className="text-xs font-semibold text-slate-700 mb-2">
                  In-Charge Roster for Selected Park:
                </div>
                <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-3">
                  {assignedPersonnelList.map((emp) => (
                    <div
                      key={emp.id}
                      className="flex items-center justify-between rounded-md border border-slate-100 bg-sky-50/50 p-2 text-xs"
                    >
                      <div className="min-w-0 pr-2">
                        <div className="font-semibold text-slate-900 truncate">{emp.full_name}</div>
                        <div className="text-[11px] text-slate-500 font-mono">{emp.designation || "In-Charge"}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* PARK-WISE IN-CHARGE DETAILS */}
        <Card>
          <CardHeader className="pb-3 border-b border-slate-100">
            <CardTitle className="flex items-center gap-2 text-base font-semibold text-slate-900">
              <TreePine className="h-5 w-5 text-sky-700" />
              Park-wise In-Charge Details ({dashboardData?.park_wise_details?.length ?? 0})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm text-left border-collapse" data-testid="park-details-table">
              <thead className="bg-slate-100 text-[11px] uppercase tracking-wider text-slate-700 border-b border-slate-200">
                <tr>
                  <th className="p-3">Park Code</th>
                  <th className="p-3">Park Name</th>
                  <th className="p-3">Assigned In-Charge Personnel</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Date-wise Assignments</th>
                </tr>
              </thead>
              <tbody>
                {loadingDashboard ? (
                  <tr>
                    <td colSpan={5} className="p-6 text-center text-sm text-slate-500">Loading details…</td>
                  </tr>
                ) : (dashboardData?.park_wise_details || []).length === 0 ? (
                  <tr>
                    <td colSpan={5} className="p-6 text-center text-sm text-slate-500">No park details match the current filter.</td>
                  </tr>
                ) : (
                  dashboardData.park_wise_details.map((row) => (
                    <tr key={row.park_id} className="border-b border-slate-100 hover:bg-slate-50/80" data-testid={`park-row-${row.park_code}`}>
                      <td className="p-3 font-mono font-semibold text-slate-800">{row.park_code}</td>
                      <td className="p-3 font-medium text-slate-900">{row.park_name}</td>
                      <td className="p-3">
                        {row.assigned_incharge && row.assigned_incharge.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {row.assigned_incharge.map((name, idx) => (
                              <Badge key={idx} variant="outline" className="border-sky-300 bg-sky-50 text-sky-900 text-xs">
                                {name}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400 italic">Unassigned</span>
                        )}
                      </td>
                      <td className="p-3">
                        {row.status === "Assigned" ? (
                          <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-900 text-xs flex items-center gap-1 w-max">
                            <CheckCircle2 className="h-3 w-3 text-emerald-700" /> Assigned
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-900 text-xs flex items-center gap-1 w-max">
                            <Clock className="h-3 w-3 text-amber-700" /> Pending
                          </Badge>
                        )}
                      </td>
                      <td className="p-3">
                        {row.date_assignments && row.date_assignments.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {row.date_assignments.map((da) => (
                              <div key={da.id} className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5 text-xs border border-slate-200">
                                <Calendar className="h-3 w-3 text-sky-700" />
                                <span className="font-mono text-[11px]">{da.date}</span>
                                <span className="font-medium">({da.employee_name})</span>
                                <button
                                  onClick={() => handleDeleteDateAssignment(da.id)}
                                  className="ml-1 text-slate-400 hover:text-rose-600"
                                  title="Delete assignment"
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400 italic">No scheduled dates</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}
